import { Avatar, Box, Checkbox, Flex, Grid, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Table, Tbody, Td, Text, Th, Thead, Tr, useToast } from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FaCheckCircle, FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp, FaClock, FaEye, FaFileDownload, FaLock, FaPaperPlane, FaShieldAlt } from 'react-icons/fa'
import { MdOutlineMarkEmailUnread, MdWarningAmber } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { ButtonCmp } from '../../../components/ButtonCmp'
import { ElementoTooltipCmp } from '../../../components/ElementoTooltipCmp'
import { NavLine } from '../../../components/NavLine'
import { SearchCmp } from '../../../components/SearchCmp'
import { SelectCmp } from '../../../components/SelectCmp'
import { WhiteContainer } from '../../../components/WhiteContainer'
import { appApi } from '../../../services/appApi'
import { Body } from '../../Layouts/Body'
import { Sidebar } from '../Gestao/components/Sidebar'
import { VersoesFechadas } from './components/VersoesFechadas'

type StatusPainel = 'Pendente' | 'Desatualizado' | 'Ajuste Solicitado' | 'Finalizado' | 'Solicitação Vencida'

interface iAssinantePainel {
  id?: string
  nome: string
  email: string
  avatar?: string
  assinado: boolean
  solicitacaoVencida: boolean
  desatualizado: boolean
  expiraEm?: string
  ultimaAcao?: string
}

interface iCargoPainel {
  cargoId: string
  nomeCargo: string
  area?: string
  nivelHierarquico?: string
  status: StatusPainel[]
  ultimaAcao?: string
  assinantes: iAssinantePainel[]
}

interface iGestorPainel {
  email: string
  nome: string
  avatar?: string
  pendentes: number
  desatualizados: number
  ajustes: number
  concluidos: number
}

interface iPendencia {
  cargo: iCargoPainel
  assinante: iAssinantePainel
}

type Coluna = 'nomeCargo' | 'area' | 'responsavel' | 'status' | 'ultimaAcao'

const coresStatus: Record<StatusPainel, { bg: string, color: string, border: string }> = {
  Pendente: { bg: 'gray.100', color: 'gray.700', border: 'gray.200' },
  Desatualizado: { bg: 'purple.50', color: 'purple.700', border: 'purple.200' },
  'Ajuste Solicitado': { bg: 'orange.50', color: 'orange.700', border: 'orange.200' },
  Finalizado: { bg: 'green.50', color: 'green.700', border: 'green.200' },
  'Solicitação Vencida': { bg: 'red.50', color: 'red.700', border: 'red.200' }
}

function formatarData (valor?: string): string {
  return valor ? new Date(valor).toLocaleDateString('pt-BR') : '-'
}

function responsavelAtual (cargo: iCargoPainel): string {
  return cargo.assinantes.filter(a => !a.assinado).map(a => a.nome).join(', ') || '-'
}

function pendenciasDe (cargos: iCargoPainel[], somenteVencidas = false): iPendencia[] {
  return cargos.flatMap(cargo => cargo.assinantes
    .filter(a => !a.assinado && (!somenteVencidas || a.solicitacaoVencida))
    .map(assinante => ({ cargo, assinante })))
}

export const PainelAssinaturas: React.FC = () => {
  const nav = useNavigate()
  const toast = useToast()
  const carrosselRef = useRef<HTMLDivElement>(null)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [IsLoading, setIsLoading] = useState(false)
  const [Cargos, setCargos] = useState<iCargoPainel[]>([])
  const [Busca, setBusca] = useState('')
  const [FiltroArea, setFiltroArea] = useState('')
  const [FiltroHierarquia, setFiltroHierarquia] = useState('')
  const [FiltroResponsavel, setFiltroResponsavel] = useState('')
  const [FiltroStatus, setFiltroStatus] = useState('')
  const [BuscaGestor, setBuscaGestor] = useState('')
  const [Ordenacao, setOrdenacao] = useState<{ coluna: Coluna, asc: boolean }>({ coluna: 'ultimaAcao', asc: false })
  const [Selecionados, setSelecionados] = useState<string[]>([])
  const [ModalVencidas, setModalVencidas] = useState(false)
  const [VencidasMarcadas, setVencidasMarcadas] = useState<string[]>([])
  const [Enviando, setEnviando] = useState(false)
  const [Fechando, setFechando] = useState<string>()
  const [VersoesKey, setVersoesKey] = useState(0)

  function getPainel (): void {
    setIsLoading(true)
    appApi.get<iCargoPainel[]>('api/Assinatura/Painel')
      .then(res => setCargos(res.data))
      .catch(err => console.log(err))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => { getPainel() }, [])

  const areas = useMemo(() => Array.from(new Set(Cargos.map(c => c.area).filter(Boolean))) as string[], [Cargos])
  const hierarquias = useMemo(() => Array.from(new Set(Cargos.map(c => c.nivelHierarquico).filter(Boolean))) as string[], [Cargos])
  const vencidas = useMemo(() => pendenciasDe(Cargos, true), [Cargos])

  const indicadores = useMemo(() => ({
    aguardando: Cargos.filter(c => !c.status.includes('Finalizado')).length,
    ajustes: Cargos.filter(c => c.status.includes('Ajuste Solicitado')).length,
    finalizados: Cargos.filter(c => c.status.includes('Finalizado')).length
  }), [Cargos])

  const todosGestores = useMemo(() => {
    const mapa = new Map<string, iGestorPainel>()
    Cargos.forEach(cargo => cargo.assinantes.forEach(a => {
      const gestor = mapa.get(a.email) ?? { email: a.email, nome: a.nome, avatar: a.avatar, pendentes: 0, desatualizados: 0, ajustes: 0, concluidos: 0 }
      if (a.assinado) gestor.concluidos++
      else if (cargo.status.includes('Ajuste Solicitado')) gestor.ajustes++
      else if (a.desatualizado) gestor.desatualizados++
      else gestor.pendentes++
      mapa.set(a.email, gestor)
    }))
    return Array.from(mapa.values())
  }, [Cargos])

  const gestores = useMemo(
    () => todosGestores.filter(g => g.nome.toLowerCase().includes(BuscaGestor.toLowerCase())),
    [todosGestores, BuscaGestor])

  const primeiroEUltimoNome = (nome: string): string => {
    const partes = nome.trim().split(/\s+/)
    return partes.length > 2 ? `${partes[0]} ${partes[partes.length - 1]}` : nome
  }

  const linhas = useMemo(() => {
    const termo = Busca.toLowerCase()
    const valor = (c: iCargoPainel): string => {
      if (Ordenacao.coluna === 'responsavel') return responsavelAtual(c)
      if (Ordenacao.coluna === 'status') return c.status.join(', ')
      return c[Ordenacao.coluna] ?? ''
    }
    return Cargos
      .filter(c => (c.nomeCargo.toLowerCase().includes(termo) || responsavelAtual(c).toLowerCase().includes(termo)) &&
        (!FiltroArea || c.area === FiltroArea) &&
        (!FiltroHierarquia || c.nivelHierarquico === FiltroHierarquia) &&
        (!FiltroResponsavel || c.assinantes.some(a => a.email === FiltroResponsavel)) &&
        (!FiltroStatus || c.status.includes(FiltroStatus as StatusPainel)))
      .sort((a, b) => valor(a).localeCompare(valor(b)) * (Ordenacao.asc ? 1 : -1))
  }, [Cargos, Busca, FiltroArea, FiltroHierarquia, FiltroResponsavel, FiltroStatus, Ordenacao])

  async function reenviar (pendencias: iPendencia[]): Promise<void> {
    if (pendencias.length === 0) return
    setEnviando(true)
    // Reaproveita o reenvio da tela do cargo: cada chamada gera um código novo para o assinante
    const resultados = await Promise.allSettled(pendencias.map(async ({ cargo, assinante }) => await appApi.post('api/Assinatura/cadastrar', {
      documentoId: cargo.cargoId,
      nomeAssinante: assinante.nome,
      emailAssinante: assinante.email,
      colaboradorId: assinante.id
    })))
    const falhas = resultados.filter(r => r.status === 'rejected').length
    toast({
      title: falhas === 0 ? 'Solicitações reenviadas com sucesso' : `${falhas} de ${pendencias.length} solicitações não puderam ser reenviadas`,
      status: falhas === 0 ? 'success' : 'warning',
      duration: 4000,
      position: 'top'
    })
    setEnviando(false)
    setSelecionados([])
    setModalVencidas(false)
    getPainel()
  }

  function exportar (): void {
    const cabecalho = ['Cargo', 'Área', 'Hierarquia', 'Assinaturas pendentes', 'Status', 'Última ação']
    const corpo = linhas.map(c => [c.nomeCargo, c.area ?? '', c.nivelHierarquico ?? '', responsavelAtual(c), c.status.join(' | '), formatarData(c.ultimaAcao)])
    const csv = [cabecalho, ...corpo].map(l => l.map(v => `"${v.replace(/"/g, '""')}"`).join(';')).join('\n')
    const link = document.createElement('a')
    link.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }))
    link.download = 'painel-assinaturas.csv'
    link.click()
  }

  function fecharVersao (cargo: iCargoPainel): void {
    setFechando(cargo.cargoId)
    appApi.post('api/CargosESalarios/FecharVersao', { CargoId: cargo.cargoId })
      .then(() => {
        toast({ title: `Versão do cargo ${cargo.nomeCargo} fechada com sucesso`, status: 'success', duration: 4000, position: 'top' })
        setVersoesKey(k => k + 1)
        getPainel()
      })
      .catch(err => {
        const mensagem = err?.response?.data?.mensagem
        if (err?.response?.status === 400 && mensagem) toast({ title: mensagem, status: 'warning', duration: 6000, position: 'top' })
        else console.log(err)
      })
      .finally(() => setFechando(undefined))
  }

  function ordenar (coluna: Coluna): void {
    setOrdenacao(o => ({ coluna, asc: o.coluna === coluna ? !o.asc : true }))
  }

  function abrirModalVencidas (): void {
    setVencidasMarcadas(vencidas.map(v => v.cargo.cargoId + v.assinante.email))
    setModalVencidas(true)
  }

  const CabecalhoOrdenavel: React.FC<{ coluna: Coluna, titulo: string }> = ({ coluna, titulo }) => (
    <Th cursor={'pointer'} onClick={() => ordenar(coluna)} userSelect={'none'}>
      <Flex alignItems={'center'} gap={'.25rem'}>
        {titulo}
        <Box opacity={Ordenacao.coluna === coluna ? 1 : 0.25}>
          {Ordenacao.coluna === coluna && Ordenacao.asc ? <FaChevronUp /> : <FaChevronDown />}
        </Box>
      </Flex>
    </Th>
  )

  const Indicador: React.FC<{ titulo: string, valor: number, legenda: string, cor: string, icone: React.ReactNode }> = ({ titulo, valor, legenda, cor, icone }) => (
    <WhiteContainer padding='1.5rem'>
      <Flex alignItems={'center'} gap={'.6rem'} mb={'.75rem'} color={cor}>
        {icone}
        <Text fontSize={'14px'} fontWeight={'600'}>{titulo}</Text>
      </Flex>
      <Text fontSize={'36px'} fontWeight={'700'} color={cor} lineHeight={'1'}>{valor}</Text>
      <Text fontSize={'12px'} color={'gray.500'} mt={'.4rem'}>{legenda}</Text>
    </WhiteContainer>
  )

  return (
    <Flex justifyContent={'center'} alignItems={'self-start'} pos={'relative'} gap={'2rem'} margin={'0 auto'} pr={'2rem'}>
      <Flex
        position="sticky"
        top="0"
        left="0"
        direction="column"
        bg="white"
        transition="width 0.3s"
        width={isCollapsed ? '60px' : '220px'}
        flexShrink={0}
        height="100vh">
        <Sidebar isCollapsed={isCollapsed} onCollapse={setIsCollapsed} selecionado={'Painel de assinaturas'}/>
      </Flex>
      <Box flex={1} minW={0}>
      <Body isLoading={IsLoading} style={{ width: '100%' }} width={'100%'}>
        <Flex flexDir={'column'} gap={'1rem'} mb={'2rem'}>
          <Flex justifyContent={'space-between'} gap={'1rem'} flexWrap={'wrap'}>
            <NavLine options={[{ nome: 'Cargos', path: '/Descritivo/Cargos' }, { nome: 'Painel de assinaturas' }]} />
            <ButtonCmp VarColor='c6' onClick={() => nav(-1)}>Voltar</ButtonCmp>
          </Flex>
          <Flex justifyContent={'space-between'} alignItems={'end'} gap={'1rem'} flexWrap={'wrap'}>
            <Box>
              <h1>Painel de assinaturas</h1>
              <Text color={'gray.500'}>Acompanhamento das assinaturas dos descritivos de cargo.</Text>
            </Box>
            <ButtonCmp VarColor='Green2' leftIcon={<FaFileDownload />} onClick={exportar}>Exportar dados</ButtonCmp>
          </Flex>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }} gap={'1rem'}>
            <Indicador titulo='Aguardando ação' valor={indicadores.aguardando} legenda='Cargos em fluxo de assinatura' cor='var(--Azul)' icone={<FaClock />} />
            <WhiteContainer padding='1.5rem'>
              <Flex alignItems={'center'} gap={'.6rem'} mb={'.75rem'} color={'red.600'}>
                <MdOutlineMarkEmailUnread size={18} />
                <Text fontSize={'14px'} fontWeight={'600'}>Solicitações vencidas</Text>
              </Flex>
              <Text fontSize={'36px'} fontWeight={'700'} color={'red.600'} lineHeight={'1'}>{vencidas.length}</Text>
              {vencidas.length > 0 && (
                <ButtonCmp mt={'.75rem'} w={'100%'} size={'sm'} VarColor='Red' rightIcon={<FaPaperPlane />} onClick={abrirModalVencidas}>Reenviar em lote</ButtonCmp>
              )}
            </WhiteContainer>
            <Indicador titulo='Ajustes solicitados' valor={indicadores.ajustes} legenda='Retornaram ao RH' cor='orange.500' icone={<MdWarningAmber size={18} />} />
            <Indicador titulo='Finalizados' valor={indicadores.finalizados} legenda='Assinados por todos os responsáveis' cor='green.600' icone={<FaShieldAlt />} />
          </Grid>

          <WhiteContainer padding='1.5rem'>
            <Flex justifyContent={'space-between'} alignItems={'center'} mb={'1rem'} gap={'1rem'} flexWrap={'wrap'}>
              <Flex alignItems={'center'} gap={'.75rem'}>
                <h3>Operação por responsável</h3>
                <Text fontSize={'12px'} fontWeight={'700'} bg={'gray.100'} px={'.6rem'} py={'.2rem'} borderRadius={'full'}>{gestores.length} responsáveis</Text>
              </Flex>
              <Flex gap={'.5rem'} alignItems={'center'}>
                <SearchCmp placeholder='Buscar responsável...' width={'min(14rem, 100%)'} OnChange={setBuscaGestor} EnterAction={() => {}} />
                <ButtonCmp small VarColor='c6' onClick={() => carrosselRef.current?.scrollBy({ left: -340, behavior: 'smooth' })}><FaChevronLeft /></ButtonCmp>
                <ButtonCmp small VarColor='c6' onClick={() => carrosselRef.current?.scrollBy({ left: 340, behavior: 'smooth' })}><FaChevronRight /></ButtonCmp>
              </Flex>
            </Flex>
            <Flex ref={carrosselRef} gap={'1rem'} overflowX={'auto'} pb={'.5rem'} sx={{ scrollSnapType: 'x mandatory', '&::-webkit-scrollbar': { display: 'none' } }}>
              {gestores.map(g => {
                const total = g.pendentes + g.desatualizados + g.ajustes + g.concluidos
                return (
                  <Flex key={g.email} flexDir={'column'} minW={'340px'} w={'340px'} flexShrink={0} border={'1px solid'} borderColor={'gray.200'} borderRadius={'1rem'} p={'1.25rem'} sx={{ scrollSnapAlign: 'start' }}>
                    <Flex alignItems={'center'} gap={'.75rem'} mb={'1rem'}>
                      <Avatar size={'md'} name={g.nome} src={g.avatar} />
                      <Box minW={0} flex={1}>
                        <Text fontWeight={'700'}>{primeiroEUltimoNome(g.nome)}</Text>
                        <Text fontSize={'12px'} color={'gray.500'} noOfLines={1} title={g.email}>{g.email}</Text>
                      </Box>
                    </Flex>
                    <Flex justifyContent={'space-between'} fontSize={'12px'} color={'gray.600'} mb={'.25rem'}>
                      <Text>Status da fila</Text>
                      <Text>{total} cargos</Text>
                    </Flex>
                    <Flex h={'8px'} borderRadius={'full'} overflow={'hidden'} bg={'gray.100'} mb={'1rem'}>
                      <Box bg={'green.500'} w={`${(g.concluidos / total) * 100}%`} />
                      <Box bg={'orange.400'} w={`${(g.ajustes / total) * 100}%`} />
                      <Box bg={'purple.400'} w={`${(g.desatualizados / total) * 100}%`} />
                      <Box bg={'gray.400'} w={`${(g.pendentes / total) * 100}%`} />
                    </Flex>
                    <Grid templateColumns={'repeat(4, auto)'} justifyContent={'space-between'} textAlign={'center'} bg={'gray.50'} borderRadius={'.75rem'} py={'.75rem'} px={'.75rem'} columnGap={'.75rem'} mb={'1rem'}>
                      <Box><Text fontWeight={'700'}>{g.pendentes}</Text><Text fontSize={'10px'} color={'gray.500'}>PENDENTE</Text></Box>
                      <Box><Text fontWeight={'700'} color={'purple.600'}>{g.desatualizados}</Text><Text fontSize={'10px'} color={'purple.600'}>DESATUALIZADO</Text></Box>
                      <Box><Text fontWeight={'700'} color={'orange.500'}>{g.ajustes}</Text><Text fontSize={'10px'} color={'orange.500'}>AJUSTE</Text></Box>
                      <Box><Text fontWeight={'700'} color={'green.600'}>{g.concluidos}</Text><Text fontSize={'10px'} color={'green.600'}>CONCLUÍDO</Text></Box>
                    </Grid>
                    {g.pendentes + g.desatualizados > 0 && (
                      <ButtonCmp mt={'auto'} VarColor='Azul' leftIcon={<FaPaperPlane />} isLoading={Enviando}
                        onClick={() => { void reenviar(pendenciasDe(Cargos).filter(p => p.assinante.email === g.email && !p.cargo.status.includes('Ajuste Solicitado'))) }}>
                        Cobrar assinaturas pendentes
                      </ButtonCmp>
                    )}
                  </Flex>
                )
              })}
              {gestores.length === 0 && <Text w={'100%'} textAlign={'center'} py={'2rem'} color={'gray.500'}>Nenhum responsável encontrado.</Text>}
            </Flex>
          </WhiteContainer>

          <WhiteContainer padding='1.5rem'>
            <Flex justifyContent={'space-between'} alignItems={'center'} mb={'1rem'} gap={'1rem'} flexWrap={'wrap'}>
              <h3>Cargos em fluxo</h3>
              {Selecionados.length > 0 && (
                <Flex alignItems={'center'} gap={'.75rem'} bg={'blue.50'} border={'1px solid'} borderColor={'blue.200'} borderRadius={'.75rem'} px={'1rem'} py={'.4rem'}>
                  <Text fontSize={'14px'} fontWeight={'700'}>{Selecionados.length} selecionados</Text>
                  <ButtonCmp small VarColor='Azul' leftIcon={<FaPaperPlane />} isLoading={Enviando}
                    onClick={() => { void reenviar(pendenciasDe(Cargos.filter(c => Selecionados.includes(c.cargoId)))) }}>
                    Cobrar / reenviar em lote
                  </ButtonCmp>
                  <ButtonCmp small OutlineColor='c6' onClick={() => setSelecionados([])}>Cancelar</ButtonCmp>
                </Flex>
              )}
            </Flex>
            <Flex gap={'1rem'} mb={'1rem'} alignItems={'end'} flexWrap={'wrap'}>
              <SearchCmp placeholder='Buscar por cargo ou responsável...' width={'min(18rem, 100%)'} OnChange={setBusca} EnterAction={() => {}} />
              <SelectCmp title='Área' w={{ base: '100%', sm: '12rem' }} value={FiltroArea} OnChange={setFiltroArea}>
                <option value=''>Todas</option>
                {areas.map(a => <option key={a} value={a}>{a}</option>)}
              </SelectCmp>
              <SelectCmp title='Hierarquia' w={{ base: '100%', sm: '12rem' }} value={FiltroHierarquia} OnChange={setFiltroHierarquia}>
                <option value=''>Todas</option>
                {hierarquias.map(h => <option key={h} value={h}>{h}</option>)}
              </SelectCmp>
              <SelectCmp title='Responsável' w={{ base: '100%', sm: '12rem' }} value={FiltroResponsavel} OnChange={setFiltroResponsavel}>
                <option value=''>Todos</option>
                {todosGestores.map(g => <option key={g.email} value={g.email}>{primeiroEUltimoNome(g.nome)}</option>)}
              </SelectCmp>
              <SelectCmp title='Status' w={{ base: '100%', sm: '12rem' }} value={FiltroStatus} OnChange={setFiltroStatus}>
                <option value=''>Todos</option>
                {Object.keys(coresStatus).map(s => <option key={s} value={s}>{s}</option>)}
              </SelectCmp>
            </Flex>
            <Box overflowX={'auto'}>
              <Table size={'sm'} minW={'900px'}>
                <Thead>
                  <Tr>
                    <Th w={'2rem'}>
                      <Checkbox isChecked={linhas.length > 0 && Selecionados.length === linhas.length}
                        onChange={e => setSelecionados(e.target.checked ? linhas.map(l => l.cargoId) : [])} />
                    </Th>
                    <CabecalhoOrdenavel coluna='nomeCargo' titulo='Cargo' />
                    <CabecalhoOrdenavel coluna='area' titulo='Área / Hierarquia' />
                    <CabecalhoOrdenavel coluna='responsavel' titulo='Assinaturas pendentes' />
                    <Th>Assinaturas</Th>
                    <CabecalhoOrdenavel coluna='status' titulo='Status' />
                    <CabecalhoOrdenavel coluna='ultimaAcao' titulo='Última ação' />
                    <Th textAlign={'right'}>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {linhas.map(c => {
                    const selecionado = Selecionados.includes(c.cargoId)
                    return (
                      <Tr key={c.cargoId} bg={selecionado ? 'blue.50' : undefined} _hover={{ bg: 'blue.50' }}>
                        <Td>
                          <Checkbox isChecked={selecionado}
                            onChange={() => setSelecionados(s => selecionado ? s.filter(id => id !== c.cargoId) : [...s, c.cargoId])} />
                        </Td>
                        <Td fontWeight={'700'}>{c.nomeCargo}</Td>
                        <Td>
                          <Text>{c.area ?? '-'}</Text>
                          <Text fontSize={'12px'} color={'gray.500'}>{c.nivelHierarquico}</Text>
                        </Td>
                        <Td>{responsavelAtual(c)}</Td>
                        <Td fontWeight={'700'} color={c.assinantes.every(a => a.assinado) ? 'green.600' : 'gray.700'}>
                          {c.assinantes.filter(a => a.assinado).length}/{c.assinantes.length}
                        </Td>
                        <Td>
                          <Flex gap={'.4rem'} flexWrap={'wrap'}>
                            {c.status.map(s => (
                              <Text key={s} fontSize={'12px'} fontWeight={'600'} px={'.6rem'} py={'.15rem'} borderRadius={'full'} border={'1px solid'}
                                bg={coresStatus[s].bg} color={coresStatus[s].color} borderColor={coresStatus[s].border}>
                                {s}
                              </Text>
                            ))}
                          </Flex>
                        </Td>
                        <Td>{formatarData(c.ultimaAcao)}</Td>
                        <Td>
                          <Flex justifyContent={'end'} gap={'.4rem'}>
                            {c.status.includes('Finalizado') && (
                              <ElementoTooltipCmp bg={'var(--Azul)'} label={'Fechar versão'}>
                                <ButtonCmp small VarColor='Green2' isLoading={Fechando === c.cargoId} onClick={() => fecharVersao(c)}>
                                  <FaLock />
                                </ButtonCmp>
                              </ElementoTooltipCmp>
                            )}
                            {!c.status.includes('Finalizado') && (
                              <ElementoTooltipCmp bg={'var(--Azul)'} label={'Reenviar solicitação'}>
                                <ButtonCmp small VarColor='Azul' isLoading={Enviando}
                                  onClick={() => { void reenviar(pendenciasDe([c])) }}>
                                  <FaPaperPlane />
                                </ButtonCmp>
                              </ElementoTooltipCmp>
                            )}
                            {c.status.includes('Ajuste Solicitado') && (
                              <ElementoTooltipCmp bg={'var(--Azul)'} label={'Analisar ajustes'}>
                                <ButtonCmp small VarColor='Rosa' onClick={() => nav(`/Descritivo/Cargos/${c.cargoId}/visualizarSolicitacoes`, { state: { voltarPara: '/Descritivo/PainelAssinaturas' } })}>
                                  <MdWarningAmber />
                                </ButtonCmp>
                              </ElementoTooltipCmp>
                            )}
                            <ElementoTooltipCmp bg={'var(--Azul)'} label={'Visualizar cargo'}>
                              <ButtonCmp small VarColor='Green2' onClick={() => nav(`/Descritivo/Cargos/${c.cargoId}`)}>
                                <FaEye />
                              </ButtonCmp>
                            </ElementoTooltipCmp>
                          </Flex>
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
              {linhas.length === 0 && <Text textAlign={'center'} py={'2rem'} color={'gray.500'}>Nenhum cargo encontrado para os filtros selecionados.</Text>}
            </Box>
          </WhiteContainer>

          <VersoesFechadas key={VersoesKey} />
        </Flex>
      </Body>
      </Box>

      <Modal isOpen={ModalVencidas} onClose={() => setModalVencidas(false)} size={'xl'} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reenviar solicitações vencidas</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize={'14px'} color={'gray.600'} mb={'1rem'}>Selecione as solicitações que receberão um novo código de assinatura.</Text>
            <Flex flexDir={'column'} gap={'.5rem'}>
              {vencidas.map(v => {
                const chave = v.cargo.cargoId + v.assinante.email
                return (
                  <Flex key={chave} alignItems={'center'} gap={'.75rem'} border={'1px solid'} borderColor={'gray.200'} borderRadius={'.75rem'} p={'.75rem'}>
                    <Checkbox isChecked={VencidasMarcadas.includes(chave)}
                      onChange={() => setVencidasMarcadas(m => m.includes(chave) ? m.filter(k => k !== chave) : [...m, chave])} />
                    <Box flex={1}>
                      <Text fontWeight={'700'}>{v.cargo.nomeCargo}</Text>
                      <Text fontSize={'12px'} color={'gray.500'}>{v.assinante.nome}</Text>
                    </Box>
                    <Text fontSize={'12px'} color={'red.600'} fontWeight={'600'}>Venceu em {formatarData(v.assinante.expiraEm)}</Text>
                  </Flex>
                )
              })}
            </Flex>
          </ModalBody>
          <ModalFooter gap={'.5rem'}>
            <ButtonCmp OutlineColor='c6' onClick={() => setModalVencidas(false)}>Cancelar</ButtonCmp>
            <ButtonCmp VarColor='Red' leftIcon={<FaCheckCircle />} isLoading={Enviando} isDisabled={VencidasMarcadas.length === 0}
              onClick={() => { void reenviar(vencidas.filter(v => VencidasMarcadas.includes(v.cargo.cargoId + v.assinante.email))) }}>
              Reenviar {VencidasMarcadas.length} solicitações
            </ButtonCmp>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  )
}
